/**
 * Stellt sicher, dass bei Wallee-Zahlungsarten (wie TWINT) der in den
 * WooCommerce-Einstellungen konfigurierte Beschreibungstext auf der Kasse
 * angezeigt wird.
 *
 * Hintergrund:
 * Das Wallee-Plugin registriert im Checkout-Block zwar die Zahlungsarten,
 * uebergibt die Beschreibung jedoch nur an die `edit`-Eigenschaft fuer den
 * Gutenberg-Editor. Im Frontend-Akkordeon rendert Wallee ausschliesslich den
 * iFrame-Container, der bei TWINT leer bleibt.
 *
 * Dieses Skript ueberwacht das Einhaengen des Akkordeon-Inhalts und fuegt
 * die passende Beschreibung automatisch ein.
 */

import { trackHeaderHeight } from '../../functions/header-offset';

declare global {
  interface Window {
    customCheckoutConfig?: {
      paymentDescriptions?: Record<string, string>;
      shippingEmphasis?: string[];
    };
    wc?: {
      wcBlocksRegistry?: {
        getPaymentMethods?: () => Record<string, {
          edit?: {
            props?: {
              children?: string;
            };
          };
        }>;
      };
    };
  }
}

function getPaymentDescription(methodId: string): string {
  // 1. Server-seitig uebergebene WooCommerce-Gateway-Beschreibungen
  const serverDesc = window.customCheckoutConfig?.paymentDescriptions?.[methodId];
  if (typeof serverDesc === 'string' && serverDesc.trim() !== '') {
    return serverDesc.trim();
  }

  // 2. Fallback: Aus der wcBlocksRegistry des Wallee-Plugins auslesen
  const registered = window.wc?.wcBlocksRegistry?.getPaymentMethods?.();
  const editChildren = registered?.[methodId]?.edit?.props?.children;
  if (typeof editChildren === 'string' && editChildren.trim() !== '') {
    return editChildren.trim();
  }

  return '';
}

function injectPaymentDescriptions(): void {
  const contentContainers = document.querySelectorAll<HTMLElement>(
    '.wc-block-components-radio-control-accordion-content'
  );

  for (const container of contentContainers) {
    const idAttr = container.id;
    if (idAttr === '') continue;

    // Format: radio-control-wc-payment-method-options-{methodId}__content
    const match = /^radio-control-wc-payment-method-options-(.+)__content$/.exec(idAttr);
    if (match === null) continue;

    const methodId = match[1];
    if (methodId === undefined || methodId === '') continue;

    // Nur bei Wallee-Zahlungsarten eingreifen (andere wie BACS rendern ihre Beschreibung selbst)
    if (!methodId.startsWith('wallee_')) continue;

    const description = getPaymentDescription(methodId);
    if (description === '') continue;

    // Pruefen, ob die Beschreibung bereits vorhanden ist
    if (container.querySelector('.c-payment-description') !== null) continue;

    const descEl = document.createElement('p');
    descEl.className = 'c-payment-description wc-block-components-payment-method-description';
    descEl.textContent = description;

    container.prepend(descEl);
  }
}

/**
 * Setzt in der Zeile unter einer Versandart einzelne Worte fett ("3 % Rabatt").
 *
 * Der Block zeigt die Zeile (`delivery_time` der Rate) als reinen Text; ein
 * <strong> aus PHP stuende woertlich da. Welche Worte, kommt aus PHP
 * (shipping/discount.php). Schreibt React die Zeile neu, ist das <strong> weg
 * und der naechste Durchlauf des Observers setzt es wieder.
 */
function emphasizeShippingNotes(): void {
  const phrases = window.customCheckoutConfig?.shippingEmphasis ?? [];
  if (phrases.length === 0) return;

  const notes = document.querySelectorAll<HTMLElement>(
    '.wc-block-components-shipping-rates-control .wc-block-components-radio-control__description'
  );

  for (const note of notes) {
    if (note.querySelector('strong') !== null) continue;

    const text = note.textContent ?? '';
    for (const phrase of phrases) {
      const index = text.indexOf(phrase);
      if (index === -1) continue;

      const strong = document.createElement('strong');
      strong.textContent = phrase;
      note.replaceChildren(text.slice(0, index), strong, text.slice(index + phrase.length));
      break;
    }
  }
}

let noticeObserver: ResizeObserver | null = null;
let currentObservedNotice: HTMLElement | null = null;

function trackNoticeHeight(): void {
  const notices = document.querySelector<HTMLElement>('.wc-block-components-notices');
  const apply = (): void => {
    if (notices === null || notices.children.length === 0 || notices.offsetHeight === 0) {
      document.documentElement.style.removeProperty('--checkout-notice-offset');
      return;
    }
    const offset = 19 + notices.offsetHeight;
    document.documentElement.style.setProperty('--checkout-notice-offset', `${offset}px`);
  };

  apply();

  if (notices !== null && notices !== currentObservedNotice && typeof ResizeObserver !== 'undefined') {
    if (noticeObserver !== null) {
      noticeObserver.disconnect();
    }
    noticeObserver = new ResizeObserver(apply);
    noticeObserver.observe(notices);
    currentObservedNotice = notices;
  }
}

let hasScrolledToTopForNotice = false;

function ensureNoticeVisible(): void {
  if (hasScrolledToTopForNotice) return;
  const notices = document.querySelector<HTMLElement>('.wc-block-components-notices');
  if (notices !== null && notices.children.length > 0 && notices.offsetHeight > 0) {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    hasScrolledToTopForNotice = true;
  }
}

function initCheckout(): void {
  trackHeaderHeight();
  trackNoticeHeight();
  injectPaymentDescriptions();
  emphasizeShippingNotes();
  ensureNoticeVisible();

  const observer = new MutationObserver(() => {
    trackNoticeHeight();
    injectPaymentDescriptions();
    emphasizeShippingNotes();
    ensureNoticeVisible();
  });

  observer.observe(document.body, {
    childList : true,
    subtree   : true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCheckout);
} else {
  initCheckout();
}
